import { copyToClipboard } from "../../services/Utils/ClipboardUtil";
import { Trans, t } from '@lingui/macro';
import { Star, Wand2, FileText, Trash2, Copy, StarOff, Pencil, ListTodo, BookOpen } from "lucide-react";
import { IconMoreVertical } from './FigmaIcons';
import React, { useState } from "react";
import { Favorite } from "../../models/Favorite";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { getDisplayName } from "../../services/Utils/userDisplayName";
import { RoomRefreshEmitter } from "../rooms/RoomEvents";
import { MessageItemState } from "./MessageItem";
import { UIMessage } from "./UIMessage";
import { MemoModalEmitter } from "../rooms/right-menu/PanelMemos";
import { TaskEmitter } from "../tasks/TaskEmitter";
import { WikiPageEmitter } from "../board/WikiPageEmitter";
import { PostEmitter } from "../board/PostEmitter";
import { fetchWikiBoardForChannel, checkExistingTasksByThread, checkExistingWikiPagesByThread } from "../../services/BoardService";
import { Board, Post } from "../../models/Board";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

export const MessagePopover: React.FC<{ m: UIMessage, state: MessageItemState, onEdit?: () => void, onSummarize?: (text: string) => void }> = ({ m, state, onEdit, onSummarize }) => {
  const { toast } = useToast();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  /**
   * 채널 위키 게시판 존재 여부. 드롭다운 열릴 때 1회 조회해 라벨 분기에 사용.
   * - 있음 → "위키 페이지로 저장"
   * - 없음 → "위키 게시판 시작하기"
   */
  const [wikiBoard, setWikiBoard] = useState<Board | null | undefined>(undefined);
  const checkedWikiRef = React.useRef(false);
  const ensureWikiBoardChecked = React.useCallback(() => {
    if (checkedWikiRef.current) return;
    checkedWikiRef.current = true;
    fetchWikiBoardForChannel(m.msg.roomId)
      .then(setWikiBoard)
      .catch(() => setWikiBoard(null));
  }, [m.msg.roomId]);

  // 이 메시지로 만든 태스크/위키 목록 (메뉴 열 때마다 갱신 — 생성 직후 반영).
  // 같은 메시지라도 metadata.source_scope('message'|'thread') 로 메시지/쓰레드 위키·태스크를 구분.
  const [existingTasks, setExistingTasks] = useState<Post[]>([]);
  const [existingWikis, setExistingWikis] = useState<Post[]>([]);
  const checkExistingItems = React.useCallback(() => {
    checkExistingTasksByThread(m.msg.id, m.msg.roomId).then(setExistingTasks).catch(() => setExistingTasks([]));
    checkExistingWikiPagesByThread(m.msg.id, m.msg.roomId).then(setExistingWikis).catch(() => setExistingWikis([]));
  }, [m.msg.id, m.msg.roomId]);

  async function addToMemo(mode: 'create' | 'text-assistant') {
    let text = '';
    if (m.children.length > 0) {
      text = UtilApp.getThreadText(m);
    } else {
      const dateTime = UtilApp.dateStrToLocaleDateString(m.msg.created);
      const whoWrote = t`${getDisplayName(m.msg.user)} wrote`;
      text = `${dateTime} - ${whoWrote}:\n\n${m.msg.text}`;
    }
    MemoModalEmitter.emit(mode, text, m.msg.roomId);
  }

  async function addToFavorites() {
    await Fire.msg.saveFavorite(new Favorite(m.msg, My.user.id));
    toast({ title: t`Added to my saved messages` });
  }

  async function removeFromFavorites() {
    await Fire.msg.deleteFavorite(My.user.id, m.msg.id);
    toast({ title: t`Removed from my saved messages` });
  }

  async function deleteMessage() {
    if (m.msg) {
      try {
        await Fire.msg.deleteMessage(m.msg.id);
        My.DeletedMessages.set(m.msg.id, true);
        RoomRefreshEmitter.emit(m.msg.roomId);
        toast({ title: t`Message deleted` });
      } catch (e: any) {
        toast({
          title: e.message || t`Failed to delete message`,
          variant: 'destructive',
          duration: 3000
        });
      }
    }
    setShowDeleteAlert(false);
  }

  async function copyToClipBoard() {
    if (m.msg?.text) {
      _copyToClipBoard(m.msg?.text);
    }
  }

  /**
   * 쓰레드 전체 텍스트. 쓰레드가 펼쳐지지 않아 답글(children)이 비어 있으면
   * Fire.msg.getChildrenMessages 로 답글을 강제 로드해 전체를 구성한 뒤 반환.
   * (복사·위키 저장·태스크 만들기의 "쓰레드" 선택이 공유)
   */
  async function getFullThreadText(): Promise<string> {
    if (m.replyCount > 0 && (m.children?.length ?? 0) === 0) {
      try {
        const replies = await Fire.msg.getChildrenMessages(My.user.id, m.msg.id);
        return UtilApp.getThreadText(m.withChildren(replies.map(r => new UIMessage(r, []))));
      } catch (e) {
        console.warn('[MessagePopover] 쓰레드 답글 로드 실패:', e);
      }
    }
    return UtilApp.getThreadText(m);
  }

  async function copyAllToClipBoard() {
    const allText = await getFullThreadText();
    _copyToClipBoard(allText);
  }

  async function _copyToClipBoard(msgText: string) {
    console.log('copying to clipboard', msgText);
    await copyToClipboard(msgText);
    toast({
      title: t`Copied to clipboard`,
      duration: 1500,
    });
  }

  return (
    <>
      <DropdownMenu onOpenChange={(open) => { if (open) { ensureWikiBoardChecked(); checkExistingItems(); } }}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 p-0 text-[color:rgb(var(--msg-icon))] hover:text-[color:rgb(var(--msg-icon-hover))]" aria-label={t`Message options`}>
            <IconMoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[70]">
          {m.isLocal && <></>}
          {!m.isLocal &&
            <>
              {(() => {
                const room = My.Rooms.get(m.msg.roomId);
                const isReadOnly = room?.isReadOnly === true;
                return <>
                  {state == 'favorite' && (
                    <DropdownMenuItem onClick={removeFromFavorites}>
                      <StarOff className="mr-2 h-4 w-4" />
                      <span><Trans>Remove from saved</Trans></span>
                    </DropdownMenuItem>
                  )}
                  {state != 'favorite' && <>
                    {m.msg.text && !isReadOnly && room?.workspaceId && (() => {
                      const msgTask = existingTasks.find(p => p.metadata?.source_scope === 'message') ?? null;
                      const threadTask = existingTasks.find(p => p.metadata?.source_scope === 'thread') ?? null;
                      // 단일 메시지(답글 없음) — 메시지 항목만 (생성 or 보기)
                      if (m.replyCount === 0) {
                        return msgTask ? (
                          <DropdownMenuItem onClick={() => TaskEmitter.emit('open', m.msg.roomId, msgTask.id)}>
                            <ListTodo className="mr-2 h-4 w-4" />
                            <span><Trans>View task</Trans></span>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => TaskEmitter.emit('create', m.msg.text ?? '', m.msg.roomId, m.msg.id, 'message')}>
                            <ListTodo className="mr-2 h-4 w-4" />
                            <span><Trans>Create Task</Trans></span>
                          </DropdownMenuItem>
                        );
                      }
                      // 쓰레드 → 하위메뉴 [메시지 / 쓰레드], 각 항목은 생성됨이면 보기 / 아니면 생성
                      return (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <ListTodo className="mr-2 h-4 w-4" />
                            <span><Trans>Create Task</Trans></span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {msgTask ? (
                              <DropdownMenuItem onClick={() => TaskEmitter.emit('open', m.msg.roomId, msgTask.id)}>
                                <span><Trans>View message task</Trans></span>
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => TaskEmitter.emit('create', m.msg.text ?? '', m.msg.roomId, m.msg.id, 'message')}>
                                <span><Trans>Create from message</Trans></span>
                              </DropdownMenuItem>
                            )}
                            {threadTask ? (
                              <DropdownMenuItem onClick={() => TaskEmitter.emit('open', m.msg.roomId, threadTask.id)}>
                                <span><Trans>View thread task</Trans></span>
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => getFullThreadText().then(t => TaskEmitter.emit('create', t, m.msg.roomId, m.msg.id, 'thread'))}>
                                <span><Trans>Create from thread</Trans></span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      );
                    })()}
                    {/* 위키 페이지로 저장 — 위키 게시판 유무에 따라 라벨 분기
                        - 있음: 즉시 모달
                        - 없음: 위키 탭 이동 + 시작하기 후 모달 (RoomPage 가 처리) */}
                    {m.msg.text && !isReadOnly && room?.workspaceId && room.visibility !== 'direct' && (() => {
                      const msgWiki = existingWikis.find(p => p.metadata?.source_scope === 'message') ?? null;
                      const threadWiki = existingWikis.find(p => p.metadata?.source_scope === 'thread') ?? null;
                      const wikiCreateLabel = wikiBoard === null ? <Trans>Start team wiki</Trans> : <Trans>Save as wiki page</Trans>;
                      // 단일 메시지(답글 없음) — 메시지 항목만 (생성 or 보기)
                      if (m.replyCount === 0) {
                        return msgWiki ? (
                          <DropdownMenuItem onClick={() => PostEmitter.emit('open', m.msg.roomId, msgWiki.board_id, msgWiki.id)}>
                            <BookOpen className="mr-2 h-4 w-4" />
                            <span><Trans>View wiki page</Trans></span>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => WikiPageEmitter.emit('create', m.msg.text ?? '', m.msg.roomId, m.msg.id, 'message')}>
                            <BookOpen className="mr-2 h-4 w-4" />
                            <span>{wikiCreateLabel}</span>
                          </DropdownMenuItem>
                        );
                      }
                      // 쓰레드 → 하위메뉴 [메시지 / 쓰레드], 각 항목은 생성됨이면 보기 / 아니면 생성
                      return (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <BookOpen className="mr-2 h-4 w-4" />
                            <span>{wikiCreateLabel}</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {msgWiki ? (
                              <DropdownMenuItem onClick={() => PostEmitter.emit('open', m.msg.roomId, msgWiki.board_id, msgWiki.id)}>
                                <span><Trans>View message wiki</Trans></span>
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => WikiPageEmitter.emit('create', m.msg.text ?? '', m.msg.roomId, m.msg.id, 'message')}>
                                <span><Trans>Create from message</Trans></span>
                              </DropdownMenuItem>
                            )}
                            {threadWiki ? (
                              <DropdownMenuItem onClick={() => PostEmitter.emit('open', m.msg.roomId, threadWiki.board_id, threadWiki.id)}>
                                <span><Trans>View thread wiki</Trans></span>
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => getFullThreadText().then(t => WikiPageEmitter.emit('create', t, m.msg.roomId, m.msg.id, 'thread'))}>
                                <span><Trans>Create from thread</Trans></span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      );
                    })()}
                    {!isReadOnly && m.msg.text && <>
                      <DropdownMenuItem onClick={() => addToMemo('text-assistant')}>
                        <Wand2 className="mr-2 h-4 w-4" />
                        <span><Trans>Text Assistant</Trans></span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => addToMemo('create')}>
                        <FileText className="mr-2 h-4 w-4" />
                        <span><Trans>Create a Memo</Trans></span>
                      </DropdownMenuItem>
                    </>}
                    {!isReadOnly && (
                      <DropdownMenuItem onClick={addToFavorites}>
                        <Star className="mr-2 h-4 w-4" />
                        <span><Trans>Save message</Trans></span>
                      </DropdownMenuItem>
                    )}
                    {m.msg?.user.id == My.user.id && m.msg.text && onEdit && (
                      <DropdownMenuItem onClick={onEdit}>
                        <Pencil className="mr-2 h-4 w-4" />
                        <span><Trans>Edit message</Trans></span>
                      </DropdownMenuItem>
                    )}
                    {m.msg?.user.id == My.user.id && (
                      <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-destructive focus:text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span><Trans>Delete message</Trans></span>
                      </DropdownMenuItem>
                    )}
                  </>}
                  {(m.msg?.text && !UtilApp.isWorkAppWebView() && m.replyCount > 0) ? (
                    /* 메시지 + 쓰레드 둘 다 가능 → "텍스트를 복사" 하위메뉴 [메시지 / 쓰레드] */
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Copy className="mr-2 h-4 w-4" />
                        <span><Trans>Copy text to clipboard</Trans></span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={copyToClipBoard}>
                          <span><Trans>Copy message</Trans></span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={copyAllToClipBoard}>
                          <span><Trans>Copy thread</Trans></span>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (m.msg?.text && !UtilApp.isWorkAppWebView()) ? (
                    <DropdownMenuItem onClick={copyToClipBoard}>
                      <Copy className="mr-2 h-4 w-4" />
                      <span><Trans>Copy text to clipboard</Trans></span>
                    </DropdownMenuItem>
                  ) : m.replyCount > 0 ? (
                    <DropdownMenuItem onClick={copyAllToClipBoard}>
                      <Copy className="mr-2 h-4 w-4" />
                      <span><Trans>Copy thread to clipboard</Trans></span>
                    </DropdownMenuItem>
                  ) : null}

                  {/* AI 요약 — 복사와 동일 패턴 (메시지 / 쓰레드) */}
                  {onSummarize && (
                    (m.msg?.text && m.replyCount > 0) ? (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <Wand2 className="mr-2 h-4 w-4" />
                          <span><Trans>Summarize</Trans></span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => onSummarize(m.msg.text!)}>
                            <span><Trans>Summarize message</Trans></span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={async () => onSummarize(await getFullThreadText())}>
                            <span><Trans>Summarize thread</Trans></span>
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : m.msg?.text ? (
                      <DropdownMenuItem onClick={() => onSummarize(m.msg.text!)}>
                        <Wand2 className="mr-2 h-4 w-4" />
                        <span><Trans>Summarize</Trans></span>
                      </DropdownMenuItem>
                    ) : m.replyCount > 0 ? (
                      <DropdownMenuItem onClick={async () => onSummarize(await getFullThreadText())}>
                        <Wand2 className="mr-2 h-4 w-4" />
                        <span><Trans>Summarize thread</Trans></span>
                      </DropdownMenuItem>
                    ) : null
                  )}

                </>;
              })()}
            </>
          }
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className="z-[70]">
          {(m.replyCount > 0 || m.nonReactions().length > 0) ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Cannot delete this message</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This message has replies. Please delete replies first.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction className="h-10">
                  <Trans>OK</Trans>
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Do you want to delete this message?</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="mt-0 h-10"><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={deleteMessage} className="bg-destructive hover:bg-destructive/90 h-10">
                  <Trans>Delete</Trans>
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default MessagePopover;
// async function flagContent() {
//   if (m.msg) {
//     present({
//       cssClass: 'report-alert',
//       header: t`Report offensive content`,
//       message: t`Would you like to report this content as offensive?`,
//       buttons: [
//         t`Cancel`,
//         {
//           text: t`Report now`, handler: async () => {
//             close();
//             await Fire.reportContent(m.msg);
//             present({
//               header: t`Thank you for your report.`,
//               buttons: [t`OK`]
//             });
//           }
//         }
//       ]
//     })
//   }
// }